import type { OrderDetails } from '@/entities/order/model'
import type { CrateType } from '@/features/order/model/orderDetails.types'
import { Box, Divider, Flex, Group, Paper, Space, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconCircleCheck, IconCircleX } from '@tabler/icons-react'
import { getEmoji, getVanSeq } from '@/entities/order/lib'
import { OrderMetaInfoCheck, OrderMetaInfoVehicle, OrderStatusDisplay } from '@/entities/order/ui'
import { useCrateEmojisEnabled } from '@/entities/state/model'
import { getOrderSecondaryMessage, getOrderTypeWithCollectionWindow, getRapidOrderStatus, getRestrictedItemsDisplay, useOrderAlert } from '@/features/order/lib'
import { formatTime } from '@/shared/lib/dayjs'
import { normaliseEmojiItems } from '@/shared/lib/emoji'
import classes from './OrderDetailsMobileLayout.module.css'

const EMPTY_MISSING_CRATE_TYPES: string[] = []

interface OrderDetailsMobileLayoutProps {
  order: OrderDetails
  totalCrates: number
  scannedCrates?: number
  crateTypes: CrateType[]
  withoutBackdrop?: boolean
  showScanProgress?: boolean
  /** BD-8588: replace crate list with licence-plate waiting message */
  showLicencePlateWaitingCard?: boolean
  licencePlateWaitingMessage?: string
  enableMissingMarking?: boolean
  missingCrateTypes?: string[]
  onCrateClick?: (crate: CrateType) => void
  onStatusPress?: () => void
  statusDrawerOpened?: boolean
}

export function OrderDetailsMobileLayout({
  order,
  crateTypes,
  totalCrates,
  scannedCrates = 0,
  withoutBackdrop,
  showScanProgress = false,
  showLicencePlateWaitingCard = false,
  licencePlateWaitingMessage,
  enableMissingMarking = false,
  missingCrateTypes = EMPTY_MISSING_CRATE_TYPES,
  onCrateClick,
  onStatusPress,
  statusDrawerOpened = false,
}: OrderDetailsMobileLayoutProps) {
  const { displayState } = useOrderAlert(order)
  const { statusText, statusDisplayState, isRapidOrderContext } = getRapidOrderStatus({
    order,
    displayState,
    withoutBackdrop,
  })

  const { vanNumber, sequenceNumber } = getVanSeq(order.hsFields)
  const crateEmojisEnabled = useCrateEmojisEnabled()
  const emojiItems = normaliseEmojiItems(getEmoji(order.hsFields))

  return (
    <>
      <Paper className={classes.orderInfoCard} radius="md" p="md">
        <Flex direction="column" gap="lg">
          <Group justify="space-between" align="flex-start">
            <Stack gap="4px">
              <Text span className={classes.orderNumberLabel}>Order number</Text>
              <Text span className={classes.orderNumberValue}>{order.orderId}</Text>
            </Stack>
            {onStatusPress
              ? (
                  <UnstyledButton
                    onClick={onStatusPress}
                    className={classes.statusPressable}
                    data-onboarding="order-status-trigger"
                  >
                    <Group gap={4} align="center" wrap="nowrap">
                      <OrderStatusDisplay
                        orderStatus={statusText}
                        displayState={statusDisplayState}
                        secondaryText={isRapidOrderContext ? undefined : getOrderSecondaryMessage(
                          order.status,
                          order.lastTransitionTime ? formatTime(order.lastTransitionTime, 'HH:mm') : undefined,
                        )}
                        eta={order.eta}
                        etaDirection={order.etaDirection}
                        scheduledTimeForCollection={order.scheduledTimeForCollection}
                      />
                      {statusDrawerOpened
                        ? <IconChevronUp size={18} color="var(--mantine-color-gray-8)" data-testid="status-chevron-up" />
                        : <IconChevronDown size={18} color="var(--mantine-color-gray-8)" data-testid="status-chevron-down" />}
                    </Group>
                  </UnstyledButton>
                )
              : (
                  <OrderStatusDisplay
                    orderStatus={statusText}
                    displayState={statusDisplayState}
                    secondaryText={isRapidOrderContext ? undefined : getOrderSecondaryMessage(
                      order.status,
                      order.lastTransitionTime ? formatTime(order.lastTransitionTime, 'HH:mm') : undefined,
                    )}
                    eta={order.eta}
                    etaDirection={order.etaDirection}
                    scheduledTimeForCollection={order.scheduledTimeForCollection}
                  />
                )}
          </Group>

          <Group gap="sm">
            {(vanNumber || sequenceNumber) && (
              <OrderMetaInfoVehicle
                vanNumber={vanNumber}
                sequenceNumber={sequenceNumber}
                size="lg"
                bg="gray.1"
              />
            )}

            {(emojiItems.length > 0) && (
              <OrderMetaInfoCheck
                items={emojiItems}
                size="lg"
                bg="gray.1"
              />
            )}
          </Group>
        </Flex>
      </Paper>

      <Paper
        className={classes.crateCard}
        radius="md"
        data-testid={showLicencePlateWaitingCard ? 'licence-plate-waiting-card' : undefined}
        data-onboarding="tutorial-crate-progress"
      >
        <Group grow justify="space-between" p="md" bg="gray.8" className={classes.crateCardHeader}>
          {showLicencePlateWaitingCard
            ? <Space />
            : <Text span className={classes.crateItemType}>Total crates</Text>}
          {showLicencePlateWaitingCard
            ? <Space />
            : (
                <Text span className={classes.crateItemCount}>
                  {showScanProgress ? `${scannedCrates}/${totalCrates}` : totalCrates}
                </Text>
              )}
          <Space />
        </Group>

        <Box p="lg">
          {showLicencePlateWaitingCard
            ? (
                <Text span className={classes.licencePlateWaitingMessage}>
                  {licencePlateWaitingMessage}
                </Text>
              )
            : (
                crateTypes.map((crate, index) => {
                  const scanned = crate.scannedCount ?? 0
                  const isMissing = missingCrateTypes.includes(crate.type)
                  const isComplete = showScanProgress && !isMissing && crate.count > 0 && scanned >= crate.count
                  const isClickable = enableMissingMarking && !isComplete
                  const countDisplay = showScanProgress ? `${scanned}/${crate.count}` : String(crate.count)
                  const trailing = crateEmojisEnabled && crate.icon ? (
                    <Text span className={classes.crateItemIcon}>{crate.icon}</Text>
                  ) : (
                    <Space />
                  )

                  const missingRow = (
                    <Box
                      style={{
                        border: '1.5px solid var(--mantine-color-red-6)',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}
                    >
                      <Group grow justify="space-between">
                        <Group gap="xs" align="center" wrap="nowrap">
                          <IconCircleX size={24} color="var(--mantine-color-red-6)" style={{ flexShrink: 0 }} />
                          <Text span fw={700} className={classes.crateItemType}>{crate.type}</Text>
                        </Group>
                        <Text span fw={700} className={classes.crateItemCount}>{countDisplay}</Text>
                        {trailing}
                      </Group>
                    </Box>
                  )

                  const rowContent = isMissing ? (
                    enableMissingMarking
                      ? (
                          <UnstyledButton onClick={() => onCrateClick?.(crate)} style={{ width: '100%' }}>
                            {missingRow}
                          </UnstyledButton>
                        )
                      : missingRow
                  ) : isComplete ? (
                    <Box
                      style={{
                        border: '1.5px solid var(--mantine-color-green-6)',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}
                    >
                      <Group grow justify="space-between">
                        <Group gap="xs" align="center" wrap="nowrap">
                          <IconCircleCheck size={24} color="var(--mantine-color-green-6)" style={{ flexShrink: 0 }} />
                          <Text span fw={700} className={classes.crateItemType}>{crate.type}</Text>
                        </Group>
                        <Text span fw={700} className={classes.crateItemCount}>{countDisplay}</Text>
                        {trailing}
                      </Group>
                    </Box>
                  ) : isClickable ? (
                    <UnstyledButton onClick={() => onCrateClick?.(crate)} style={{ width: '100%' }}>
                      <Group grow justify="space-between">
                        <Text span className={classes.crateItemType} style={{ textDecoration: 'underline' }}>{crate.type}</Text>
                        <Text span className={classes.crateItemCount}>{countDisplay}</Text>
                        {trailing}
                      </Group>
                    </UnstyledButton>
                  ) : (
                    <Group grow justify="space-between">
                      <Text span className={classes.crateItemType}>{crate.type}</Text>
                      <Text span className={classes.crateItemCount}>{countDisplay}</Text>
                      {trailing}
                    </Group>
                  )

                  return (
                    <Box
                      key={crate.type}
                      className={classes.crateItemGroup}
                      data-crate-row-state={
                        !showScanProgress
                          ? 'default'
                          : isComplete
                            ? 'complete'
                            : isMissing
                              ? 'missing'
                              : 'partial'
                      }
                    >
                      {/* Spotlight only the row — exclude Divider so Step 7 does not bleed into the next crate. */}
                      <Box data-onboarding={crate.type === 'Bulk Ambient' ? 'tutorial-bulk-ambient-row' : undefined}>
                        {rowContent}
                      </Box>
                      {index < crateTypes.length - 1 && <Divider variant="dashed" my="sm" />}
                    </Box>
                  )
                })
              )}
        </Box>
      </Paper>

      <Paper className={classes.modalCard}>
        <Text span className={classes.modalCardLabel}>Type</Text>
        <Text span className={classes.modalCardValue}>
          {getOrderTypeWithCollectionWindow(order.hsFields)}
        </Text>
      </Paper>

      <Paper className={classes.modalCard} mb="md">
        <Text span className={classes.modalCardLabel}>Restricted items</Text>
        <Text span className={classes.modalCardValue}>{getRestrictedItemsDisplay(order.hsFields)}</Text>
      </Paper>
    </>
  )
}
